/**
 * Policies list toolbar — responsive (OR-1664, design OR-1537).
 *
 * Desktop (≥ md):
 *   [☰ Filters]  [⚙ Customize]                    [Search input]
 *
 * Mobile (< md):
 *   [☰] [⚙] [Search input — fills the row]
 *
 * The search placeholder names what the backend actually matches:
 * policy number, named insured and brokerage.
 */
import { FilterLines, SearchLg, Settings04 } from "@untitledui/icons";
import { Input, cx } from "@tricura/atlas-ui";

interface PoliciesToolbarProps {
    search: string;
    onSearchChange: (next: string) => void;
    onOpenFilters: () => void;
    onOpenCustomize: () => void;
    /** Active-filter count rendered as a badge on the Filters button. */
    filterCount?: number;
    /** Held while the user's saved layout is still loading, so an edit
     *  made before it lands is neither dropped nor overwritten. */
    isDisabled?: boolean;
}

const SEARCH_PLACEHOLDER = "Search by Policy number, Name Insured, Brokerage";

export function PoliciesToolbar({ search, onSearchChange, onOpenFilters, onOpenCustomize, filterCount = 0, isDisabled = false }: PoliciesToolbarProps) {
    return (
        <div className="border-secondary bg-primary border-b px-4 py-3">
            {/* ---------- Desktop layout (≥ md) ---------- */}
            <div className="hidden items-center gap-2 md:flex">
                <ToolbarButton icon={FilterLines} label="Filters" badge={filterCount} isDisabled={isDisabled} data-autotest="policies-toolbar-button-filters" onClick={onOpenFilters} />
                <ToolbarButton icon={Settings04} label="Customize" isDisabled={isDisabled} data-autotest="policies-toolbar-button-customize" onClick={onOpenCustomize} />
                <div className="ml-auto w-96">
                    <Input
                        size="sm"
                        icon={SearchLg}
                        placeholder={SEARCH_PLACEHOLDER}
                        value={search}
                        onChange={onSearchChange}
                        aria-label="Search policies"
                        data-autotest="policies-toolbar-input-search"
                    />
                </div>
            </div>

            {/* ---------- Mobile layout (< md) ---------- */}
            <div className="flex items-center gap-2 md:hidden">
                <IconBtn icon={FilterLines} label="Filters" badge={filterCount} isDisabled={isDisabled} data-autotest="policies-toolbar-button-filters" onClick={onOpenFilters} />
                <IconBtn icon={Settings04} label="Customize" isDisabled={isDisabled} data-autotest="policies-toolbar-button-customize" onClick={onOpenCustomize} />
                <div className="min-w-0 flex-1">
                    <Input
                        size="sm"
                        icon={SearchLg}
                        placeholder={SEARCH_PLACEHOLDER}
                        value={search}
                        onChange={onSearchChange}
                        aria-label="Search policies"
                        data-autotest="policies-toolbar-input-search"
                    />
                </div>
            </div>
        </div>
    );
}

const PILL =
    "border-primary inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border bg-primary px-3 text-sm font-semibold transition duration-100 ease-linear";

interface ToolbarButtonProps {
    icon: typeof FilterLines;
    label: string;
    badge?: number;
    onClick: () => void;
    isDisabled?: boolean;
    "data-autotest": string;
}

function ToolbarButton({ icon: Icon, label, badge, onClick, isDisabled, "data-autotest": dataAutotest }: ToolbarButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            data-autotest={dataAutotest}
            className={cx(PILL, "text-primary shadow-xs hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50")}
        >
            <Icon aria-hidden="true" className="size-4 text-fg-quaternary stroke-[2.25px]" />
            {label}
            {badge != null && badge > 0 && <CountBadge value={badge} />}
        </button>
    );
}

function IconBtn({ icon: Icon, label, badge, onClick, isDisabled, "data-autotest": dataAutotest }: ToolbarButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            aria-label={label}
            data-autotest={dataAutotest}
            className={cx(PILL, "text-primary shadow-xs hover:bg-secondary relative size-9 justify-center px-0 disabled:cursor-not-allowed disabled:opacity-50")}
        >
            <Icon aria-hidden="true" className="size-4 text-fg-quaternary stroke-[2.25px]" />
            {badge != null && badge > 0 && (
                <span className="absolute -right-1 -top-1">
                    <CountBadge value={badge} />
                </span>
            )}
        </button>
    );
}

function CountBadge({ value }: { value: number }) {
    return (
        <span className="bg-brand-solid text-white inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none">
            {value}
        </span>
    );
}
